import React, { useEffect, useState } from 'react';
import axios from 'axios';
import IntranetLayout from './IntranetLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Pencil, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PROGRAMMES = ['kids', 'neo', 'ruby', 'archy', 'elite'];
const STATUSES = [
  { value: 'en_attente', label: 'En attente', color: 'bg-amber-100 text-amber-700' },
  { value: 'validee', label: 'Validée', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'expiree', label: 'Expirée', color: 'bg-red-100 text-red-700' },
];

const emptyForm = { student_id: '', programme: 'kids', type: 'period', date_debut: '', date_fin: '', sessions_total: 0, sessions_used: 0, status: 'en_attente' };

export const IntranetInscriptions = () => {
  const [inscriptions, setInscriptions] = useState([]);
  const [students, setStudents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [filterStatus, setFilterStatus] = useState('all');

  const fetchAll = async () => {
    const [iRes, sRes] = await Promise.all([
      axios.get(`${API}/intranet/inscriptions`),
      axios.get(`${API}/intranet/students`),
    ]);
    setInscriptions(iRes.data);
    setStudents(sRes.data);
  };
  useEffect(() => { fetchAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, sessions_total: parseInt(form.sessions_total) || 0, sessions_used: parseInt(form.sessions_used) || 0 };
      if (editingId) {
        await axios.put(`${API}/intranet/inscriptions/${editingId}`, payload);
        toast.success('Inscription modifiée');
      } else {
        await axios.post(`${API}/intranet/inscriptions`, payload);
        toast.success('Inscription créée');
      }
      setShowForm(false); setEditingId(null); setForm(emptyForm); fetchAll();
    } catch { toast.error('Erreur'); }
  };

  const handleEdit = (i) => { setForm(i); setEditingId(i.id); setShowForm(true); };
  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette inscription ?')) return;
    await axios.delete(`${API}/intranet/inscriptions/${id}`);
    toast.success('Supprimée'); fetchAll();
  };

  const getStudentName = (id) => { const s = students.find(s => s.id === id); return s ? `${s.prenom} ${s.nom}` : id; };
  const getStatusBadge = (status) => {
    const s = STATUSES.find(st => st.value === status);
    return <span className={`text-xs font-bold px-2 py-1 rounded-full ${s?.color || 'bg-slate-100'}`}>{s?.label || status}</span>;
  };

  const filtered = filterStatus === 'all' ? inscriptions : inscriptions.filter(i => i.status === filterStatus);

  return (
    <IntranetLayout>
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h2 className="font-heading text-2xl font-extrabold text-slate-900">Inscriptions</h2>
            <p className="text-sm text-slate-500">{inscriptions.length} inscriptions</p>
          </div>
          <Button onClick={() => { setShowForm(true); setEditingId(null); setForm(emptyForm); }} className="btn-primary" data-testid="add-inscription-btn">
            <Plus size={16} className="mr-1" /> Nouvelle inscription
          </Button>
        </div>

        {/* Filter */}
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setFilterStatus('all')} className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${filterStatus === 'all' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'}`}>Toutes</button>
          {STATUSES.map(s => (
            <button key={s.value} onClick={() => setFilterStatus(s.value)} className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${filterStatus === s.value ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'}`}>{s.label}</button>
          ))}
        </div>

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
            <div className="bg-white rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-heading text-lg font-extrabold">{editingId ? 'Modifier' : 'Nouvelle'} inscription</h3>
                <button onClick={() => setShowForm(false)}><X size={20} /></button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-3" data-testid="inscription-form">
                <div>
                  <Label className="text-xs font-semibold mb-1 block">Élève *</Label>
                  <Select value={form.student_id} onValueChange={v => setForm({...form, student_id: v})}>
                    <SelectTrigger className="rounded-lg" data-testid="select-student-insc"><SelectValue placeholder="Choisir un élève" /></SelectTrigger>
                    <SelectContent>{students.map(s => <SelectItem key={s.id} value={s.id}>{s.prenom} {s.nom}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold mb-1 block">Programme</Label>
                  <Select value={form.programme} onValueChange={v => setForm({...form, programme: v})}>
                    <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
                    <SelectContent>{PROGRAMMES.map(p => <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold mb-1 block">Type</Label>
                  <Select value={form.type} onValueChange={v => setForm({...form, type: v})}>
                    <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="period">Période (date début/fin)</SelectItem>
                      <SelectItem value="sessions">Nombre de séances</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.type === 'period' ? (
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs font-semibold mb-1 block">Date début</Label><Input type="date" value={form.date_debut} onChange={e => setForm({...form, date_debut: e.target.value})} className="rounded-lg" /></div>
                    <div><Label className="text-xs font-semibold mb-1 block">Date fin</Label><Input type="date" value={form.date_fin} onChange={e => setForm({...form, date_fin: e.target.value})} className="rounded-lg" /></div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs font-semibold mb-1 block">Séances totales</Label><Input type="number" min={0} value={form.sessions_total} onChange={e => setForm({...form, sessions_total: e.target.value})} className="rounded-lg" /></div>
                    <div><Label className="text-xs font-semibold mb-1 block">Séances utilisées</Label><Input type="number" min={0} value={form.sessions_used} onChange={e => setForm({...form, sessions_used: e.target.value})} className="rounded-lg" /></div>
                  </div>
                )}
                <div>
                  <Label className="text-xs font-semibold mb-1 block">Statut</Label>
                  <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
                    <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Button type="submit" className="w-full btn-primary justify-center" data-testid="submit-inscription">{editingId ? 'Enregistrer' : 'Créer'}</Button>
              </form>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-slate-600">Élève</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600">Programme</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600">Type</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600 hidden md:table-cell">Validité</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600">Statut</th>
                <th className="text-right px-4 py-3 font-semibold text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-10 text-slate-400">Aucune inscription</td></tr>
              ) : filtered.map(i => (
                <tr key={i.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`inscription-row-${i.id}`}>
                  <td className="px-4 py-3 font-semibold text-slate-900">{getStudentName(i.student_id)}</td>
                  <td className="px-4 py-3"><span className="capitalize">{i.programme}</span></td>
                  <td className="px-4 py-3 text-slate-600">{i.type === 'period' ? 'Période' : 'Séances'}</td>
                  <td className="px-4 py-3 text-slate-600 text-xs hidden md:table-cell">
                    {i.type === 'period' ? `${i.date_debut || '?'} → ${i.date_fin || '?'}` : `${i.sessions_used}/${i.sessions_total} séances`}
                  </td>
                  <td className="px-4 py-3">{getStatusBadge(i.status)}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => handleEdit(i)} className="text-[#1B6FE3] hover:bg-blue-50 p-1.5 rounded-lg mr-1"><Pencil size={15} /></button>
                    <button onClick={() => handleDelete(i.id)} className="text-red-500 hover:bg-red-50 p-1.5 rounded-lg"><Trash2 size={15} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </IntranetLayout>
  );
};

export default IntranetInscriptions;
